import { Helmet } from "react-helmet";
import { Link as RouterLink, useNavigate, Navigate } from "react-router-dom";
import { Form, FormikProps, Formik } from "formik";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import LockRounded from "@mui/icons-material/LockRounded";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Typography from "@mui/material/Typography";
import { Alert, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { forceChangePassword, logout, selectAuth } from "./authSlice";
import { FormikTextField } from "../../components/Layout/FormikTextField";
import AuthSkeleton from "./AuthSkeleton";
import { changePasswordSchema } from "./validation";
import Toast from "../../components/Layout/Toast";
import { UpdatePassword } from "./types/authType";
import { cleanupPushOnLogout } from "../../features/notifications/pushNotifications";

export const ForceChangePassword = () => {
  const { loading, error, user } = useAppSelector(selectAuth);
  const { t: ta } = useTranslation("auth");
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  if (!user || !user.email) {
    return <Navigate to="/home" />;
  }
  if (!user.mustChangePassword) {
    return <Navigate to="/app" />;
  }

  const handleLeave = async () => {
    await cleanupPushOnLogout();
    dispatch(logout());
    navigate("/home");
  };

  return (
    <>
      <Helmet>
        <title>{`${ta("changePassword")} | ${t("platform")}`}</title>
      </Helmet>
      <Box
        sx={(theme) => ({
          minHeight: "100vh",
          background: `linear-gradient(150deg, ${
            (theme.palette.primary as any).dark ?? theme.palette.primary.main
          } 0%, ${theme.palette.primary.main} 45%, ${
            theme.palette.secondary.main
          } 100%)`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 5,
          px: 2,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle at 15% 85%, rgba(255,255,255,0.07) 0%, transparent 45%), radial-gradient(circle at 85% 15%, rgba(255,255,255,0.09) 0%, transparent 45%)",
            pointerEvents: "none",
          },
        })}
      >
        <Stack
          spacing={3}
          alignItems="center"
          sx={{ width: "100%", maxWidth: 440, position: "relative", zIndex: 1 }}
        >
          <Box
            component={RouterLink}
            to="/home"
            onClick={(e) => {
              e.preventDefault();
              void handleLeave();
            }}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <img
              src="/cvPlusLogo.png"
              alt="CvPlus"
              style={{ height: 52, objectFit: "contain" }}
            />
          </Box>

          <Card
            elevation={12}
            sx={{ width: "100%", borderRadius: 3, overflow: "hidden" }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Stack sx={{ mb: 3, alignItems: "center" }}>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                  {ta("changePassword")}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {user.email}
                </Typography>
              </Stack>

              <Alert severity="warning" sx={{ mb: 2 }}>
                {ta("forcePasswordChangeNotice")}
              </Alert>

              {loading === "pending" ? (
                <AuthSkeleton />
              ) : (
                <Formik
                  initialValues={{
                    userId: user._id ?? "",
                    oldPassword: "",
                    password: "",
                    confirmPassword: "",
                  }}
                  validationSchema={changePasswordSchema}
                  onSubmit={(values, actions) => {
                    actions.setSubmitting(false);
                    dispatch(forceChangePassword(values)).then((action: any) => {
                      if (forceChangePassword.fulfilled.match(action)) {
                        navigate("/app");
                      }
                    });
                  }}
                >
                  {(props: FormikProps<UpdatePassword>) => (
                    <Form>
                      <FormikTextField
                        autoComplete="off"
                        formikKey="oldPassword"
                        label={ta("oldPassword")}
                        type="password"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockRounded />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <FormikTextField
                        formikKey="password"
                        label={ta("newPassword")}
                        type="password"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockRounded />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <FormikTextField
                        formikKey="confirmPassword"
                        label={ta("confirmNewPassword")}
                        type="password"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockRounded />
                            </InputAdornment>
                          ),
                        }}
                      />

                      {error && <Toast severity="error">{error.message}</Toast>}
                      <Box component="div" pt={2}>
                        <Button
                          sx={{ width: "100%", marginY: "8px" }}
                          type="submit"
                          color="secondary"
                          variant="contained"
                          disabled={!props.isValid}
                        >
                          {ta("changePassword")}
                        </Button>
                      </Box>
                    </Form>
                  )}
                </Formik>
              )}
            </CardContent>
          </Card>

          <Button
            onClick={() => void handleLeave()}
            startIcon={<ArrowBack fontSize="small" />}
            sx={{
              color: "rgba(255,255,255,0.70)",
              fontWeight: 500,
              fontSize: "0.85rem",
              "&:hover": { color: "rgba(255,255,255,1)" },
            }}
          >
            {ta("backToHomeOrLogin")}
          </Button>
        </Stack>
      </Box>
    </>
  );
};
